import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { IconButton, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../config/constants';
import { verifyResetToken, resetPassword, validatePassword } from '../services/authService';
import PublicPageFooter from '../components/PublicPageFooter';

interface FormData {
  new_password: string;
  confirm_password: string;
}

interface FormErrors {
  new_password?: string;
  confirm_password?: string;
  submit?: string;
}

interface PasswordPolicy {
  min_length: number;
  require_uppercase: boolean;
  require_lowercase: boolean;
  require_digit: boolean;
  require_special: boolean;
  special_chars: string;
  description: string;
}

interface ValidationResult {
  is_valid: boolean;
  errors: string[];
  score: number;
  strength: 'very_weak' | 'weak' | 'medium' | 'strong';
}

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const [tokenInfo, setTokenInfo] = useState<any>(null);
  const [formData, setFormData] = useState<FormData>({
    new_password: '',
    confirm_password: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate(ROUTES.LOGIN);
      return;
    }
    
    verifyToken();
    loadPasswordPolicy();
  }, [token]);

  useEffect(() => {
    if (formData.new_password) {
      validatePasswordStrength();
    } else {
      setValidation(null);
    }
  }, [formData.new_password]);

  const verifyToken = async () => {
    try {
      const response = await verifyResetToken(token!);
      setIsTokenValid(true);
      setTokenInfo(response);
    } catch (error: any) {
      setIsTokenValid(false);
      if (error.response?.data?.detail) {
        setErrors({ submit: error.response.data.detail });
      }
    }
  };

  const loadPasswordPolicy = async () => {
    try {
      const response = await fetch('/api/v1/auth/password-policy');
      const data = await response.json();
      setPasswordPolicy(data.policy);
    } catch (error) {
      console.error('Failed to load password policy:', error);
    }
  };

  const validatePasswordStrength = async () => {
    try {
      const response = await validatePassword({
        password: formData.new_password,
        email: tokenInfo?.email
      });
      setValidation(response.validation);
    } catch (error) {
      console.error('Failed to validate password:', error);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.new_password) {
      newErrors.new_password = 'Password baru harus diisi';
    } else if (validation && !validation.is_valid) {
      newErrors.new_password = 'Password tidak memenuhi kebijakan keamanan';
    }
    
    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Konfirmasi password harus diisi';
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = 'Konfirmasi password tidak sama';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setErrors({});
    
    try {
      await resetPassword({
        token: token!,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password
      });
      
      setIsSuccess(true);
      
    } catch (error: any) {
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          setErrors({ submit: error.response.data.detail });
        } else if (error.response.data.detail.validation_errors) {
          setErrors({ 
            new_password: error.response.data.detail.validation_errors.join(', '),
            submit: error.response.data.detail.message 
          });
        }
      } else {
        setErrors({ submit: 'Terjadi kesalahan. Silakan coba lagi.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrengthColor = (strength: string) => {
    switch (strength) {
      case 'very_weak': return 'error';
      case 'weak': return 'warning';
      case 'medium': return 'info';
      case 'strong': return 'success';
      default: return 'primary';
    }
  };

  const getPasswordStrengthText = (strength: string) => {
    switch (strength) {
      case 'very_weak': return 'Sangat Lemah';
      case 'weak': return 'Lemah';
      case 'medium': return 'Sedang';
      case 'strong': return 'Kuat';
      default: return '';
    }
  };

  if (!token) {
    return null;
  }

  if (isTokenValid === false) {
    return (
      <Container component="main" maxWidth="sm">
        <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="h6">Link Reset Password Tidak Valid</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {errors.submit || 'Link reset password sudah expired atau tidak valid.'}
              </Typography>
            </Alert>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/forgot-password')}
                sx={{ mr: 2 }}
              >
                Minta Link Baru
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(ROUTES.LOGIN)}
              >
                Kembali ke Login
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (isTokenValid === null) {
    return (
      <Container component="main" maxWidth="sm">
        <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Memverifikasi token...</Typography>
        </Box>
      </Container>
    );
  }

  if (isSuccess) {
    return (
      <Container component="main" maxWidth="sm">
        <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="h6">Password Berhasil Diubah!</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Password Anda telah berhasil diperbarui. Silakan login dengan password baru.
              </Typography>
            </Alert>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                onClick={() => navigate(ROUTES.LOGIN)}
                size="large"
              >
                Login Sekarang
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          {/* Logo */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <img
              src="/Mowilex-Corporate.png"
              alt="Mowilex Logo"
              style={{ maxWidth: '200px', height: 'auto', marginBottom: '16px' }}
            />
          </Box>

          <Typography component="h1" variant="h4" sx={{ mb: 2, textAlign: 'center' }}>
            Buat Password Baru
          </Typography>

          {tokenInfo && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Reset password untuk: <strong>{tokenInfo.email}</strong>
              <br />
              <Typography variant="caption">
                Link berlaku hingga: {new Date(tokenInfo.expires_at).toLocaleString('id-ID')}
              </Typography>
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {errors.submit && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.submit}
              </Alert>
            )}

            {/* New Password */}
            <TextField
              margin="normal"
              required
              fullWidth
              name="new_password"
              label="Password Baru"
              type={showPassword ? 'text' : 'password'}
              value={formData.new_password}
              onChange={handleInputChange}
              error={!!errors.new_password}
              helperText={errors.new_password}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password Strength Indicator */}
            {validation && formData.new_password && (
              <Box sx={{ mt: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ mr: 1 }}>
                    Kekuatan Password:
                  </Typography>
                  <Typography
                    variant="caption"
                    color={getPasswordStrengthColor(validation.strength)}
                    sx={{ fontWeight: 'bold' }}
                  >
                    {getPasswordStrengthText(validation.strength)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={validation.score}
                  color={getPasswordStrengthColor(validation.strength) as any}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            )}

            {/* Password Policy */}
            {passwordPolicy && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Kebijakan Password:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      {formData.new_password.length >= passwordPolicy.min_length ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseIcon color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText 
                      primary={`Minimal ${passwordPolicy.min_length} karakter`}
                      primaryTypographyProps={{
                        color: formData.new_password.length >= passwordPolicy.min_length ? 'success.main' : 'text.secondary'
                      }}
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      {/[A-Z]/.test(formData.new_password) ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseIcon color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText 
                      primary="Minimal 1 huruf besar"
                      primaryTypographyProps={{
                        color: /[A-Z]/.test(formData.new_password) ? 'success.main' : 'text.secondary'
                      }}
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      {/[a-z]/.test(formData.new_password) ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseIcon color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText 
                      primary="Minimal 1 huruf kecil"
                      primaryTypographyProps={{
                        color: /[a-z]/.test(formData.new_password) ? 'success.main' : 'text.secondary'
                      }}
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      {/\d/.test(formData.new_password) ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseIcon color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText 
                      primary="Minimal 1 angka"
                      primaryTypographyProps={{
                        color: /\d/.test(formData.new_password) ? 'success.main' : 'text.secondary'
                      }}
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      {new RegExp(`[${passwordPolicy.special_chars.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}]`).test(formData.new_password) ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseIcon color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText 
                      primary={`Minimal 1 karakter khusus (${passwordPolicy.special_chars})`}
                      primaryTypographyProps={{
                        color: new RegExp(`[${passwordPolicy.special_chars.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}]`).test(formData.new_password) ? 'success.main' : 'text.secondary'
                      }}
                    />
                  </ListItem>
                </List>
              </Box>
            )}

            {/* Confirm Password */}
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirm_password"
              label="Konfirmasi Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirm_password}
              onChange={handleInputChange}
              error={!!errors.confirm_password}
              helperText={errors.confirm_password}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading || !validation?.is_valid}
              sx={{ mt: 3, mb: 2 }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Reset Password'
              )}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link 
              component="button"
              variant="body2" 
              onClick={() => navigate(ROUTES.LOGIN)}
              sx={{ textDecoration: 'none' }}
            >
              ← Kembali ke Login
            </Link>
          </Box>
        </Paper>
      </Box>
      <PublicPageFooter />
    </Container>
  );
};

export default ResetPasswordPage;